/**
 * CountryComplianceRulesPanel — règles de conformité télématique par pays.
 *
 * Le geste visé : « nos filiales sont en France, Pologne et Suède — que dit la
 * réglementation télématique dans chacune ? ». Les pays ne se saisissent pas :
 * ils sont déduits des filiales du périmètre par la route
 * GET /gdpr-compliance/country-rules (X-Active-Org restreint à une filiale).
 *
 * Remplace l'ancien CountryCompliancePanel, qui appelait une route inexistante
 * (/country_rules) sur une table vide, avec une liste de pays et de catégories
 * codées en dur — donc faux dès que le référentiel bouge.
 *
 * ⚠️ Le contenu affiché est INDICATIF (drapeau is_advisory renvoyé par l'API) :
 * la bannière d'avertissement n'est jamais masquable.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Globe, AlertTriangle, Ban, Users, Clock, ShieldCheck, Scale, RefreshCw, Loader2, Info,
} from 'lucide-react';
import { apiRequest } from '../../api/core.js';

/** Drapeau d'un code ISO 3166-1 alpha-2, sans table codée en dur. */
const flagOf = (code) =>
  /^[A-Z]{2}$/.test(code || '')
    ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : '🏳️';

const CATEGORY_ORDER = ['telematics', 'location', 'camera', 'scoring', 'biometrics', 'retention', 'data_transfer'];

function CountryComplianceRulesPanel() {
  const { t, i18n } = useTranslation();
  const [activeCountry, setActiveCountry] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  // Le contenu des regles vient de la base, pas des fichiers de langue : c'est le
  // serveur qui le traduit. La langue courante fait donc partie de la cle de cache,
  // sinon une bascule FR/EN reafficherait les regles de la langue precedente.
  const lang = (i18n.language || 'fr').slice(0, 2).toLowerCase();

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['gdpr_country_rules', lang],
    queryFn: () => apiRequest(`/gdpr-compliance/country-rules?lang=${lang}`),
    staleTime: 5 * 60 * 1000,
  });

  const payload = data?.data || {};
  const countries = payload.countries || [];

  const current = useMemo(
    () => countries.find((c) => c.country_code === activeCountry) || countries[0] || null,
    [countries, activeCountry],
  );

  const categories = useMemo(() => {
    const present = new Set((current?.rules || []).map((r) => r.rule_category));
    return CATEGORY_ORDER.filter((c) => present.has(c));
  }, [current]);

  const rules = useMemo(() => {
    const all = current?.rules || [];
    return activeCategory ? all.filter((r) => r.rule_category === activeCategory) : all;
  }, [current, activeCategory]);

  const countryLabel = (c) => t(`compliance.countryRules.countries.${c.country_code}`, c.country_name || c.country_code);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex items-start justify-between gap-4">
        <div className="flex items-start">
          <Globe className="w-5 h-5 mr-2 mt-0.5 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold">{t('compliance.countryRules.title')}</h3>
            <p className="text-sm text-gray-500">{t('compliance.countryRules.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
          title={t('compliance.countryRules.refresh')}
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Avertissement juridique — jamais masquable */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
        <Scale className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-semibold text-amber-800">{t('compliance.countryRules.advisory.title')}</h4>
          <p className="text-sm text-amber-700 mt-1">{t('compliance.countryRules.advisory.body')}</p>
        </div>
      </div>

      {/* Périmètre : les pays viennent des filiales, pas d'une saisie */}
      {countries.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            {t('compliance.countryRules.scope', {
              count: countries.length,
              countries: countries.map((c) => countryLabel(c)).join(' · '),
            })}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          {t('compliance.countryRules.loading')}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {t('compliance.countryRules.error')}
        </div>
      ) : countries.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
          {t('compliance.countryRules.empty')}
        </div>
      ) : (
        <>
          {/* Sélecteur de pays */}
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => {
              const selected = current?.country_code === c.country_code;
              return (
                <button
                  key={c.country_code}
                  type="button"
                  onClick={() => { setActiveCountry(c.country_code); setActiveCategory(null); }}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-colors ${
                    selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="text-lg">{flagOf(c.country_code)}</span>
                  <span>{countryLabel(c)}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${selected ? 'bg-blue-500' : 'bg-gray-100 text-gray-600'}`}>
                    {c.rules_count}
                  </span>
                </button>
              );
            })}
          </div>

          {current && (
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
              {/* Bandeau pays + filiales concernées */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center">
                  <span className="text-4xl mr-4">{flagOf(current.country_code)}</span>
                  <div>
                    <h4 className="text-xl font-bold">{countryLabel(current)}</h4>
                    <p className="text-sm text-gray-500">
                      {current.orgs?.length
                        ? t('compliance.countryRules.subsidiaries', { list: current.orgs.map((o) => o.name).join(', ') })
                        : t('compliance.countryRules.noSubsidiary')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 text-sm">
                  <Stat icon={ShieldCheck} tone="text-gray-700" value={current.rules_count} label={t('compliance.countryRules.stats.rules')} />
                  <Stat icon={Ban} tone="text-red-600" value={current.prohibited_count} label={t('compliance.countryRules.stats.prohibited')} />
                  <Stat icon={Users} tone="text-amber-600" value={current.works_council_count} label={t('compliance.countryRules.stats.worksCouncil')} />
                </div>
              </div>

              {!current.has_rules && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  {t('compliance.countryRules.missingCountry')}
                </div>
              )}

              {/* Filtre par catégorie */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                  <Chip active={!activeCategory} onClick={() => setActiveCategory(null)}>
                    {t('compliance.countryRules.allCategories')}
                  </Chip>
                  {categories.map((cat) => (
                    <Chip key={cat} active={activeCategory === cat} onClick={() => setActiveCategory(cat)}>
                      {t(`compliance.countryRules.categories.${cat}`)}
                    </Chip>
                  ))}
                </div>
              )}

              {/* Règles */}
              <div className="space-y-3">
                {rules.map((rule) => (
                  <RuleCard key={rule.id} rule={rule} t={t} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, value, label, tone }) {
  return (
    <div className="text-center">
      <div className={`flex items-center justify-center gap-1 font-semibold ${tone}`}>
        <Icon className="w-4 h-4" />
        {value ?? 0}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-sm transition-colors ${
        active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

function RuleCard({ rule, t }) {
  const tone = rule.is_prohibited
    ? 'border-red-200 bg-red-50'
    : rule.requires_works_council
      ? 'border-amber-200 bg-amber-50'
      : 'border-gray-200 bg-white';

  return (
    <div className={`border rounded-lg p-4 ${tone}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {t(`compliance.countryRules.categories.${rule.rule_category}`, rule.rule_category)}
          </div>
          <h5 className="font-medium mt-0.5">{rule.rule_name}</h5>
          {rule.rule_description && (
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{rule.rule_description}</p>
          )}
          {rule.legal_reference && (
            <p className="text-xs text-gray-500 mt-2">
              <span className="font-medium">{t('compliance.countryRules.legalReference')} :</span> {rule.legal_reference}
            </p>
          )}
          {rule.notes && <p className="text-xs text-gray-500 mt-1 italic">{rule.notes}</p>}
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {rule.is_prohibited && <Badge className="bg-red-100 text-red-800">{t('compliance.countryRules.badges.prohibited')}</Badge>}
          {rule.is_mandatory && <Badge className="bg-indigo-100 text-indigo-800">{t('compliance.countryRules.badges.mandatory')}</Badge>}
          {rule.requires_works_council && <Badge className="bg-amber-100 text-amber-800">{t('compliance.countryRules.badges.worksCouncil')}</Badge>}
          {rule.requires_consent && <Badge className="bg-green-100 text-green-800">{t('compliance.countryRules.badges.consent')}</Badge>}
          {rule.max_retention_days != null && (
            <Badge className="bg-blue-100 text-blue-800">
              <Clock className="w-3 h-3 inline mr-1" />
              {t('compliance.countryRules.badges.maxRetention', { days: rule.max_retention_days })}
            </Badge>
          )}
          {rule.min_retention_days != null && (
            <Badge className="bg-blue-50 text-blue-700">
              {t('compliance.countryRules.badges.minRetention', { days: rule.min_retention_days })}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ className, children }) {
  return <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${className}`}>{children}</span>;
}

export default CountryComplianceRulesPanel;
